import React, {FC, useCallback, useMemo} from 'react';
import './HardCosts.less';
import Campaign, {CampaignHardCosts} from "../../../entities/campaign";
import EditableTable, {EditableTableProps, Justify, OnAddFn} from "../../../ui/EditableTable/EditableTable";
import {format} from "date-fns";
import {genUUID} from "../../../lib/picos/uuids";
import util from "../../../lib/util";
import HardCostsCard from "./HardCostsCard";
import {VoidCallback} from "../../../@types/functions";

/**
 * Component to edit hard costs for a campaign.
 *
 * Lists one or more items, with an associated cost.
 *
 * 11/12/2019   Design Services.....  $250
 * 11/15/2019   Printing@Kinkos.....  $150
 * 12/14/2019   Postage.............  $ 50
 * ---------------------------------------
 * [Add]                              $450
 *
 */
const HardCosts: FC<HardCostsProps> = props => {

    const handleAdd: OnAddFn<CampaignHardCosts> = useCallback((costs: CampaignHardCosts, cb: VoidCallback) => {
        const newCosts = [...props.items, costs];

        props.onChange(newCosts);
        cb();
    }, [props]);

    const handleEdit = useCallback((costs: CampaignHardCosts, cb: VoidCallback) => {
        const newCosts = [...props.items];
        const index = newCosts.findIndex(c => c.id === costs.id);
        newCosts.splice(index, 1, costs);

        props.onChange(newCosts);
        cb();
    }, [props]);

    const handleDelete = useCallback((costs: CampaignHardCosts, cb: VoidCallback) => {
        const newCosts = [...props.items];
        const index = newCosts.findIndex(c => c.id === costs.id);
        newCosts.splice(index, 1);

        props.onChange(newCosts);
        cb();
    }, [props]);

    const hardCosts = useMemo(() => {
        const costs = props.items
            .sort((a, b) => {
                const aDate = a.incurredOn ? new Date(a.incurredOn).getTime() : 0;
                const bDate = b.incurredOn ? new Date(b.incurredOn).getTime() : 0;
                return aDate - bDate;
            })
            .map(c => {
                // We are adding an ID field to this, so that eventually it can be moved into its own table.
                if (c.id) return c;
                else if (!c.id) {
                    c.id = genUUID();
                    return c;
                }
            });

        // If we added IDs, send the new costs structure back.
        if (props.items.some(hc => !hc.id)) props.onChange(costs);

        return costs;
    }, [props]);

    const renderIncurredDate = useCallback((hardCosts: CampaignHardCosts) => {
        return hardCosts.incurredOn ? format(new Date(hardCosts.incurredOn), 'MM/dd/yyyy') : '';
    }, []);

    const renderCost = useCallback((hardCosts: CampaignHardCosts) => {
        return util.formatCurrency(Number(hardCosts.cost));
    }, []);

    const HardCostsTable = EditableTable as unknown as React.ComponentType<EditableTableProps<CampaignHardCosts>>;

    return <div className='hard-costs'>
        <HardCostsTable name={`hard-costs-${props.campaign.id}`}
                        entityName='CampaignHardCosts'
                        urlQueryKey="hard-cost"
                        editor={HardCostsCard}
                        displayEntityName='Hard Cost'
                        hideGridOnEdit={true}
                        items={hardCosts}
                        properties={[
                            {label: 'incurredOn', renderItemFn: renderIncurredDate},
                            {label: 'item', width: '1fr'},
                            {label: 'cost', justify: Justify.RIGHT, renderItemFn: renderCost},
                        ]}
                        onAdd={handleAdd}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
        />
    </div>
        ;
}

export type HardCostsProps = {
    campaign: Campaign,
    items: CampaignHardCosts[];
    focusTarget?: 'item' | 'cost';

    // Used by ContentBlock
    isEditing?: boolean;
    onChange?: (costs: CampaignHardCosts[]) => void;
}

export default HardCosts;